import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, BookOpen, GraduationCap } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

interface GlossaryTermProps {
  id: string;
  term: string;
  slug: string;
  category: string;
  definition: string;
  example?: string;
  synonyms?: string[];
  englishTerm?: string;
  relatedArticles?: string[];
  relatedFormations?: string[];
}

/**
 * Composant d'affichage d'un terme du glossaire avec Schema.org DefinedTerm
 */
export function GlossaryTerm({
  id,
  term,
  slug,
  category,
  definition,
  example,
  synonyms,
  englishTerm,
  relatedArticles = [],
  relatedFormations = [],
}: GlossaryTermProps) {
  const { locale } = useParams();
  const currentLocale = locale || 'fr';

  // Schema.org DefinedTerm pour SEO
  const definedTermSchema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    '@id': `https://aspexit.com/${currentLocale}/glossaire#${slug}`,
    name: term,
    description: definition,
    ...(englishTerm && { alternateName: englishTerm }),
    ...(synonyms && synonyms.length > 0 && {
      sameAs: synonyms.map((syn) => `https://aspexit.com/${currentLocale}/glossaire#${slug}`),
    }),
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: 'Glossaire Agriculture Numérique Aspexit',
      url: `https://aspexit.com/${currentLocale}/glossaire`,
    },
  };

  return (
    <>
      {/* Schema.org DefinedTerm */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(definedTermSchema)}</script>
      </Helmet>

      <Card id={slug} className="p-6 scroll-mt-24 hover:shadow-lg transition-shadow">
        {/* Header : Terme + Catégorie */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-xl font-heading font-bold text-aspexit-black mb-1">
              {term}
            </h3>
            {englishTerm && (
              <p className="text-sm text-aspexit-gray italic">
                {englishTerm}
              </p>
            )}
          </div>
          <Badge variant="outline" className="text-aspexit-green border-aspexit-green">
            {category}
          </Badge>
        </div>

        {/* Définition */}
        <p className="text-aspexit-gray leading-relaxed mb-4">
          {definition}
        </p>

        {/* Exemple */}
        {example && (
          <div className="bg-aspexit-green/5 border-l-4 border-aspexit-green p-4 rounded-r mb-4">
            <p className="text-sm font-medium text-aspexit-black mb-1">
              💡 Exemple pratique
            </p>
            <p className="text-sm text-aspexit-gray italic">
              {example}
            </p>
          </div>
        )}

        {/* Synonymes */}
        {synonyms && synonyms.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-aspexit-gray mb-2">
              Synonymes :
            </p>
            <div className="flex flex-wrap gap-2">
              {synonyms.map((synonym, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {synonym}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Liens vers articles et formations */}
        {(relatedArticles.length > 0 || relatedFormations.length > 0) && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-xs font-medium text-aspexit-gray mb-3">
              📚 Ressources liées :
            </p>
            <div className="flex flex-wrap gap-3">
              {/* Articles */}
              {relatedArticles.map((articleSlug, index) => (
                <Link
                  key={`article-${index}`}
                  to={`/${currentLocale}/blog/${articleSlug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-aspexit-green hover:text-aspexit-green-dark hover:underline transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Article
                  <ExternalLink className="w-3 h-3" />
                </Link>
              ))}

              {/* Formations */}
              {relatedFormations.map((formationSlug, index) => (
                <Link
                  key={`formation-${index}`}
                  to={`/${currentLocale}/formations/${formationSlug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-aspexit-red hover:text-aspexit-red-dark hover:underline transition-colors"
                >
                  <GraduationCap className="w-4 h-4" />
                  Formation
                  <ExternalLink className="w-3 h-3" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
